import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { normalizeCartQuantity } from '../utils/uom';

const CartContext = createContext(null);
const CART_STORAGE_KEY = 'fnh_cart_items';
// Mirrors QuantityStepper's own max — enforced here too so every entry point
// (Add to Cart, Buy Now, repeated clicks) is capped consistently, not just
// the stepper's own +/- buttons.
const MAX_ITEM_QUANTITY = 99;

// The backend has no server-side cart — only a stateless pricing quote
// (POST /cart/quote). The cart itself lives entirely in frontend state,
// persisted to localStorage so it survives refreshes for guests and
// logged-in customers alike.
const readInitialItems = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    return parsed.flatMap((item) => {
      const quantity = normalizeCartQuantity(item?.product, item?.quantity, MAX_ITEM_QUANTITY);
      return quantity === null ? [] : [{ ...item, quantity }];
    });
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readInitialItems);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // `product` is cached (a snapshot from the product-card/detail response) so
  // the cart page and badge can render without an extra fetch; CartPage still
  // calls POST /cart/quote for authoritative pricing before checkout.
  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.productId === product._id);
      if (existing) {
        const nextQuantity = normalizeCartQuantity(
          product,
          Number(existing.quantity) + Number(quantity),
          MAX_ITEM_QUANTITY
        );
        if (nextQuantity === null) return prev;
        return prev.map((item) =>
          item.productId === product._id
            ? { ...item, quantity: nextQuantity }
            : item
        );
      }
      const nextQuantity = normalizeCartQuantity(product, quantity, MAX_ITEM_QUANTITY);
      if (nextQuantity === null) return prev;
      return [...prev, { productId: product._id, quantity: nextQuantity, product }];
    });
  }, []);

  const setQuantity = useCallback((productId, quantity) => {
    setItems((prev) => {
      const numericQuantity = Number(quantity);
      if (Number.isFinite(numericQuantity) && numericQuantity <= 0) {
        return prev.filter((item) => item.productId !== productId);
      }
      return prev.map((item) => {
        if (item.productId !== productId) return item;
        const nextQuantity = normalizeCartQuantity(item.product, quantity, MAX_ITEM_QUANTITY);
        return nextQuantity === null ? item : { ...item, quantity: nextQuantity };
      });
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((item) => item.productId !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const quantityFor = useCallback(
    (productId) => items.find((item) => item.productId === productId)?.quantity || 0,
    [items]
  );

  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, removeItem, setQuantity, clearCart, quantityFor, itemCount }),
    [items, addItem, removeItem, setQuantity, clearCart, quantityFor, itemCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
